import type { ExpenseCatalog } from '@clockwise/clients/expense-catalogs';
import { ApiError, expensePeriodLabel } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { AuditEntryList } from '@/components/custom/audit/audit-entry-list';
import { BackLink } from '@/components/custom/back-link';
import {
  DetailList,
  type DetailListRow,
  DetailListSkeleton,
} from '@/components/custom/detail-list';
import { formatCostOrVariable } from '@/components/custom/expense-catalog-form';
import { ExpenseCatalogFormSheet } from '@/components/custom/expense-catalog-form-sheet';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import { RequirePermission } from '@/components/custom/require-permission';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissionGuard } from '@/hooks/use-permission-guard';
import { auditApi, expenseCatalogsApi } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

const TAB_VALUES = ['details', 'audit'] as const;
type TabValue = (typeof TAB_VALUES)[number];

const detailSearch = z.object({
  tab: z.enum(TAB_VALUES).optional(),
});

export const Route = createFileRoute('/_authenticated/setup/expense-catalogs/$id/')({
  validateSearch: detailSearch,
  component: ExpenseCatalogDetailPage,
});

const AUDIT_PAGE_SIZE = 50;
const DEFAULT_TAB: TabValue = 'details';
const ENTITY_TYPE = 'expense_catalogs';

// Single source for the detail rows: drives both the loaded table and the
// skeleton's row count, so the two can't drift (and the layout won't jump).
const DETAIL_FIELDS: { label: string; value: (e: ExpenseCatalog) => ReactNode }[] = [
  { label: 'Name', value: (e) => e.name },
  {
    label: 'Description',
    value: (e) => e.description,
  },
  {
    // One row, not two. "Variable" IS the answer to "what does this cost", so
    // splitting it into a separate Yes/No row would make the reader assemble
    // the fact from two places — and would leave the Cost row rendering an
    // em-dash, which reads as missing data rather than a deliberate state.
    label: 'Cost',
    value: (e) =>
      e.variable ? (
        <span className="text-muted-foreground">{formatCostOrVariable(e.cost)}</span>
      ) : (
        <span className="tabular-nums">{formatCostOrVariable(e.cost)}</span>
      ),
  },
  { label: 'Period', value: (e) => expensePeriodLabel(e.period) },
  { label: 'Status', value: (e) => <StatusBadge active={e.active} /> },
  { label: 'Created', value: (e) => new Date(e.createdAt).toLocaleString() },
  { label: 'Updated', value: (e) => new Date(e.updatedAt).toLocaleString() },
];

function ExpenseCatalogDetailPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { allowed: canView, fallback } = usePermissionGuard(
    'expensecatalog.view',
    'expense catalog',
  );
  // The Audit tab reads /api/audit, which is gated by `audit.view`. Without it
  // the tab is hidden entirely rather than rendering a "Forbidden." panel.
  const canViewAudit = useHasPermission('audit.view');
  const requestedTab: TabValue = tab ?? DEFAULT_TAB;
  const activeTab: TabValue =
    requestedTab === 'audit' && !canViewAudit ? DEFAULT_TAB : requestedTab;

  const detail = useQuery({
    queryKey: queryKeys.expenseCatalogs.detail(id),
    queryFn: () => expenseCatalogsApi.get(id),
    enabled: canView,
  });

  const audit = useQuery({
    queryKey: queryKeys.audit.forEntity({
      entityType: ENTITY_TYPE,
      entityId: id,
      limit: AUDIT_PAGE_SIZE,
      offset: 0,
    }),
    queryFn: () =>
      auditApi.list({
        entityType: ENTITY_TYPE,
        entityId: id,
        limit: AUDIT_PAGE_SIZE,
        offset: 0,
      }),
    enabled: canView && canViewAudit,
  });

  const item = detail.data?.data ?? null;
  const [editOpen, setEditOpen] = useState(false);

  // Status changes are audited, so refresh both the list/detail caches and the
  // Audit tab shown on this page.
  const invalidateAfterStatusChange = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.expenseCatalogs.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };

  const deactivateMutation = useMutation({
    mutationFn: () => expenseCatalogsApi.remove(id),
    onSuccess: () => {
      toast.success('Deactivated');
      invalidateAfterStatusChange();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Deactivate failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: () => expenseCatalogsApi.restore(id),
    onSuccess: () => {
      toast.success('Reactivated');
      invalidateAfterStatusChange();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Restore failed'),
  });

  const statusBusy = deactivateMutation.isPending || restoreMutation.isPending;

  if (fallback) return fallback;

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <div>
        <BackLink to="/setup/expense-catalogs">Back to expense catalog</BackLink>
      </div>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          {detail.isLoading ? (
            <>
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-4 w-40" />
            </>
          ) : detail.isError ? (
            <h1 className="text-2xl font-semibold text-destructive">
              {detail.error instanceof ApiError ? detail.error.message : 'Failed to load.'}
            </h1>
          ) : item ? (
            <>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold">{item.name}</h1>
                <StatusBadge active={item.active} />
              </div>
              <p className="text-sm text-muted-foreground">
                {formatCostOrVariable(item.cost)} · {expensePeriodLabel(item.period)}
              </p>
            </>
          ) : (
            <h1 className="text-2xl font-semibold">Not found</h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          <RequirePermission permission="expensecatalog.edit">
            {item?.active ? (
              <Button
                variant="outline"
                disabled={statusBusy}
                onClick={() => {
                  if (confirm(`Deactivate "${item.name}"?`)) {
                    deactivateMutation.mutate();
                  }
                }}
              >
                Deactivate
              </Button>
            ) : item && !item.active ? (
              <Button
                variant="outline"
                disabled={statusBusy}
                onClick={() => restoreMutation.mutate()}
              >
                Reactivate
              </Button>
            ) : null}
            <Button disabled={!item} onClick={() => setEditOpen(true)}>
              <Pencil />
              Edit
            </Button>
          </RequirePermission>
        </div>
      </header>

      <PillTabs
        value={activeTab}
        onValueChange={(v) =>
          navigate({
            search: (s) => ({ ...s, tab: v === DEFAULT_TAB ? undefined : (v as TabValue) }),
            replace: true,
          })
        }
        className="rounded-xl border bg-card p-4"
      >
        <PillTabsList fullWidth>
          <PillTabsTrigger value="details">Details</PillTabsTrigger>
          {canViewAudit ? (
            <PillTabsTrigger value="audit">
              Audit
              {audit.data ? (
                <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                  {audit.data.meta.total}
                </span>
              ) : null}
            </PillTabsTrigger>
          ) : null}
        </PillTabsList>

        <PillTabsContent value="details" className="space-y-3">
          <div className="rounded-md border">
            {detail.isLoading ? (
              <DetailListSkeleton rows={DETAIL_FIELDS.length} />
            ) : item ? (
              <DetailList rows={detailRows(item)} />
            ) : (
              <p className="p-4 text-sm text-muted-foreground">Expense item not found.</p>
            )}
          </div>
        </PillTabsContent>

        <PillTabsContent value="audit" className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Every state-changing operation, newest first.
          </p>
          <div className="rounded-md border">
            {audit.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : audit.isError ? (
              <p className="p-4 text-sm text-destructive">
                {audit.error instanceof ApiError
                  ? audit.error.message
                  : 'Failed to load audit log.'}
              </p>
            ) : (
              <AuditEntryList items={audit.data?.data ?? []} total={audit.data?.meta.total ?? 0} />
            )}
          </div>
        </PillTabsContent>
      </PillTabs>

      <ExpenseCatalogFormSheet open={editOpen} onOpenChange={setEditOpen} item={item} />
    </PageShell>
  );
}

function detailRows(item: ExpenseCatalog): DetailListRow[] {
  return DETAIL_FIELDS.map((f) => ({ label: f.label, value: f.value(item) }));
}

function StatusBadge({ active }: { active: boolean }) {
  if (active) {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      Inactive
    </span>
  );
}
